import {
  Box,
  Button,
  Text,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  FormLabel,
  Input,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useEffect } from 'react';

import {
  typeguards as commonTypeguards,
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataLake, CreditAgreement } from '@finley-technologies/finley-types';

import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '../requests/requests.gql.generated';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type AthenaQueryExecuteModalProps = {
  organizationId: string;
  wipConfig: WipConfig;
  fullPropertyKey: string;
  athenaQuery: CreditAgreement.Config.AthenaConfig.Query;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  isOpen: boolean;
  onCancel: () => void;
  onQuerySuccess: (items: DataLake.AthenaQueryItemData[]) => void;
};
export const AthenaQueryExecuteModal = ({
  organizationId,
  wipConfig,
  fullPropertyKey,
  athenaQuery,
  presetVariables,
  inputDateType,
  isOpen,
  onCancel,
  onQuerySuccess,
}: AthenaQueryExecuteModalProps) => {
  const toast = useToast();

  const [inputDate, setInputDate] = useState<string>(
    commonDate.currentDateStr(),
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [
    executeAthenaQueryByRaw,
    { loading: isQueryLoading, data: queryData, error: queryError },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (queryError) {
      onCancel();
      const errorDescription = commonUtils.EnvUtils.isCustomerFacing()
        ? 'Incurred server error when executing Athena query'
        : queryError.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Query Execute Error',
        description: errorDescription,
      });
    }
  }, [queryError, toast, onCancel]);

  useEffect(() => {
    if (queryData && !isQueryLoading) {
      onCancel();

      const { Items } = queryData.athenaQueryResponse;
      if (Items.length === 0 || Object.keys(Items[0]).length === 0) {
        toast({
          title: 'Athena Query Execute Update',
          description: 'Query returned 0 data points',
          status: 'info',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const queryCopy = _.cloneDeep(athenaQuery);
      queryCopy.columnNames = Object.keys(Items[0]);
      wipConfig.formProps.setFieldValue(fullPropertyKey, queryCopy);

      onQuerySuccess(Items);
    }

    // !TODO @eldarshamukhamedov There is a lot of over-rendering going on here due to the credit
    // agreement object being prop-drilled from ~7 components up the component tree. Simple
    // memoization is not possible due to the really complex shape of the credit agreement object.
    // No obvious solution short of refactoring most of the athena query UI code to get rid of prop
    // drilling. Might also be necessary to simplify the credit agreement object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fullPropertyKey,
    queryData,
    toast,
    isQueryLoading,
    onCancel,
    onQuerySuccess,
  ]);

  const onFullExecute = () => {
    if (inputDate.length === 0) {
      setErrorMessage('Can not have empty date input');
      return;
    }

    if (!commonTypeguards.isFullDateStr(inputDate)) {
      setErrorMessage(`"${inputDate}" is not valid Full Date String structure`);
      return;
    }

    const { variables, bindings, query } = athenaQuery;

    const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
      { name: inputDateType, value: inputDate, parseNumber: true },
    ];

    // If there's any overlap with provided "presetVariables", prioritize the
    // value that's set up with the modal through "supplementalVariables" and
    // filter out what already existed in "presetVariables"
    const updatedPresetVariables = presetVariables.filter((preset) => {
      return preset.name !== inputDateType;
    });

    executeAthenaQueryByRaw({
      variables: {
        organizationId,
        variables: [
          ...supplementalVariables,
          ...updatedPresetVariables,
          ...variables,
        ],
        bindings,
        query: query.queryString,
      },
    });
  };

  useEffect(() => {
    setErrorMessage('');
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onCancel();
      }}
      size="lg"
    >
      <ModalOverlay />
      <ModalContent p="5">
        <ModalHeader>Execute Athena Query</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Flex alignItems="center" mb="7">
            <FormLabel w="48" m="0">
              Query Name:
            </FormLabel>
            <Text px="4">{athenaQuery.query.name}</Text>
          </Flex>

          <Box mb="7">
            <Flex alignItems="center" mb="1">
              <FormLabel w="48" m="0">
                {_.startCase(inputDateType)}:
              </FormLabel>
              <Input
                w="56"
                type="text"
                value={inputDate}
                onChange={(evt) => {
                  setInputDate(evt.target.value);
                }}
              />
            </Flex>

            <Text fontStyle="italic" fontSize="sm">
              Note: this value is only important when referencing it as a
              variable
            </Text>
          </Box>

          {errorMessage && (
            <Alert status="error" mb="7" rounded="md">
              <AlertIcon />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          <Flex justifyContent="center">
            <Button
              type="button"
              mr="5"
              isDisabled={!inputDate}
              isLoading={isQueryLoading}
              onClick={() => {
                onFullExecute();
              }}
            >
              Execute
            </Button>
            <Button onClick={() => onCancel()}>Close</Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
